import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { packageAPI } from "../../lib/api";
import UserLayout from "../../components/UserLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const AdminPackages = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        price: 0,
        credits: 0,
        is_active: true
    });

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await packageAPI.getAll();
            setPackages(response.data);
        } catch (error) {
            toast.error("Gagal memuat data paket");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (editing) {
                await packageAPI.update(editing, formData);
                toast.success("Paket berhasil diupdate");
            } else {
                await packageAPI.create(formData);
                toast.success("Paket berhasil dibuat");
            }
            setDialogOpen(false);
            fetchPackages();
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Gagal menyimpan paket");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (pkg) => {
        setEditing(pkg.id);
        setFormData({
            name: pkg.name,
            description: pkg.description,
            price: pkg.price,
            credits: pkg.credits,
            is_active: pkg.is_active
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Yakin ingin menghapus paket ini?")) return;
        
        try {
            await packageAPI.delete(id);
            toast.success("Paket berhasil dihapus");
            fetchPackages();
        } catch (error) {
            toast.error("Gagal menghapus paket");
        }
    };

    const resetForm = () => {
        setEditing(null);
        setFormData({ name: "", description: "", price: 0, credits: 0, is_active: true });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price);
    };

    return (
        <UserLayout>
            <div className="space-y-6" data-testid="admin-packages">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-gray-900">Kelola Paket</h1>
                        <p className="text-gray-600">Atur paket dan harga credits</p>
                    </div>
                    <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="rounded-xl" data-testid="add-package-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Tambah Paket
                    </Button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nama</TableHead>
                                <TableHead>Deskripsi</TableHead>
                                <TableHead>Harga</TableHead>
                                <TableHead>Credits</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                    </TableCell>
                                </TableRow>
                            ) : packages.length > 0 ? (
                                packages.map((pkg) => (
                                    <TableRow key={pkg.id}>
                                        <TableCell className="font-medium">{pkg.name}</TableCell>
                                        <TableCell className="text-gray-500 max-w-xs truncate">{pkg.description}</TableCell>
                                        <TableCell>{formatPrice(pkg.price)}</TableCell>
                                        <TableCell>{pkg.credits}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${pkg.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {pkg.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button size="icon" variant="ghost" onClick={() => handleEdit(pkg)} data-testid={`edit-${pkg.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(pkg.id)} data-testid={`delete-${pkg.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                        Belum ada paket
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                {/* Dialog Form */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{editing ? "Edit Paket" : "Tambah Paket Baru"}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Nama Paket</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="package-name-input" />
                            </div>
                            <div className="space-y-2">
                                <Label>Deskripsi</Label>
                                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="package-desc-input" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Harga (IDR)</Label>
                                    <Input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })} required data-testid="package-price-input" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Credits</Label>
                                    <Input type="number" value={formData.credits} onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) || 0 })} required data-testid="package-credits-input" />
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <Label>Status Active</Label>
                                <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                                <Button type="submit" disabled={saving} data-testid="save-package-btn">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    {saving ? "Menyimpan..." : "Simpan"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>
        </UserLayout>
    );
};

export default AdminPackages;
